/**
 * GPS collector (P4).
 *
 * Honors the configured `GpsMode` via the consent state machine in
 * `util/consent.ts`. Coordinates are only ever read with the user's awareness:
 * `silent` never prompts, `prompt`/`required` may surface the native browser
 * dialog, `off` is a hard no-op. Server-side enrichment (country/region/city,
 * impossible-travel, vpn-fallback) happens in the edge — the client only
 * reports raw lat/lon/accuracy + the resolved permission state.
 */

import type { GpsMode, GpsSignals } from "../types.ts";
import { queryGeoPermission, decideConsent, type PermissionStateValue } from "../util/consent.ts";

function empty(permission: GpsSignals["permission_state"] = null): GpsSignals {
  return { latitude: null, longitude: null, accuracy_m: null, permission_state: permission };
}

function mapPerm(p: PermissionStateValue): GpsSignals["permission_state"] {
  return p === "unavailable" ? "prompt" : p;
}

/**
 * Read a single geolocation fix within `timeoutMs`. Resolves to an empty
 * (null-coordinate) result on denial/timeout/error so the orchestrator never
 * blocks on a hesitant user.
 */
function readPosition(timeoutMs: number, startPerm: PermissionStateValue): Promise<GpsSignals> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return resolve(empty(mapPerm(startPerm)));
    }

    let settled = false;
    const finish = (v: GpsSignals) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };

    const timer = setTimeout(() => finish(empty("timeout")), timeoutMs);

    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timer);
          finish({
            latitude: pos.coords.latitude ?? null,
            longitude: pos.coords.longitude ?? null,
            accuracy_m: typeof pos.coords.accuracy === "number" ? pos.coords.accuracy : null,
            permission_state: "granted",
          });
        },
        (err) => {
          clearTimeout(timer);
          // code 1 = PERMISSION_DENIED
          const denied = err && err.code === 1;
          finish(empty(denied ? "denied" : mapPerm(startPerm)));
        },
        { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60_000 },
      );
    } catch {
      clearTimeout(timer);
      finish(empty(mapPerm(startPerm)));
    }
  });
}

/** Collect GPS signals respecting the configured mode + consent state. */
export async function collectGps(mode: GpsMode, timeoutMs = 3000): Promise<GpsSignals> {
  const perm = await queryGeoPermission();
  const decision = decideConsent(mode, perm);

  if (decision === "skip") {
    return empty(mapPerm(perm));
  }

  // Both "read-if-granted" and "may-prompt" call getCurrentPosition; the
  // browser only shows a dialog when state is "prompt".
  return await readPosition(timeoutMs, perm);
}
